/* Family-composition signup step — shown right after the inscription step for
 * registering subject roles when the study has auto-creatable DATA-ONLY roles
 * (e.g. Enfant): the first family member declares "how many?" per role, bounded
 * by the researcher-configured min/max, with the researcher-written question
 * wording. OBLIGATORY when shown (no skip path).
 *
 * SKIP LOGIC: on mount the server pre-check counts existing members sharing the
 * entered numéro de dossier — roles already populated (declared by the other
 * parent or created by the investigator) are not asked; when nothing remains,
 * the step auto-completes without rendering. The pre-check is fail-soft: on
 * error the questions are shown anyway (the server dedupes at consent).
 *
 * PERSISTENCE: like every signup value, nothing is sent here — the declarations
 * are handed up to HomeContent (pendingFamilyComposition) and ride the consent
 * body; the rows are created server-side at consent time.
 */
import { ReactElement, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Container from "../../../atoms/Container";
import { FieldError } from "../../../atoms/LoginTextField";
import NextPrevButtons from "../../../molecules/NextPrevButtons";
import Question from "../../../molecules/Question/Question";
import ResonanceLoader from "../../../atoms/ResonanceLoader/ResonanceLoader";
import useVocalisation from "../../../../hooks/useVocalisation";
import { useConfig } from "../../../../contexts/ConfigContext";
import { useHomeContext } from "../../../../pages/Home/HomeContent";
import { postFamilyPrecheck } from "../../../../services/user.service";
import { ProfileType } from "../../../../types/profile";
import {
  NumberQuestionType,
  NumberSeparatorEnum,
  QuestionTypeEnum,
} from "../../../../types/Question";

export interface FamilyCompositionDeclaration {
  study_role_id: number;
  count: number;
}

interface DisplayFamilyCompositionProps {
  // The data-only roles to declare (helper/familyComposition.ts).
  targetRoles: ProfileType[];
  // The numéro de dossier entered on the inscription step (skip pre-check key).
  externalId: string | null;
  // Back-navigation prefill.
  initialDeclarations: FamilyCompositionDeclaration[] | null;
  onCompleted: (declarations: FamilyCompositionDeclaration[]) => void;
}

const roleBounds = (role: ProfileType): { min: number; max: number } => ({
  min: role.attributes?.auto_create_min ?? 1,
  max: role.attributes?.auto_create_max ?? role.attributes?.auto_create_min ?? 1,
});

// Rendered through the real questionnaire Question component (NUMBER type) so the
// field shares the same chrome and the tel keyboard as any other number question.
const buildCountQuestion = (role: ProfileType, fallbackTitle: string): NumberQuestionType => {
  const { min, max } = roleBounds(role);
  return {
    id: -(role.id ?? 0),
    title: role.attributes?.auto_create_question_label || fallbackTitle,
    description: "",
    export_code: `family_${role.id}`,
    integral: { is_integral: false, integral_errorcode: null },
    is_mandatory_question: true,
    is_active: true,
    score_coefficient: 0,
    order: 9997,
    version: "1",
    type: QuestionTypeEnum.NUMBER,
    detail: {
      min_value: min,
      max_value: max,
      number_decimal: 0,
      prefix: "",
      suffix: "",
      separator: NumberSeparatorEnum.DOT,
    },
  };
};

const DisplayFamilyComposition = ({
  targetRoles,
  externalId,
  initialDeclarations,
  onCompleted,
}: DisplayFamilyCompositionProps): ReactElement => {
  const { t } = useTranslation();
  const { readText } = useVocalisation();
  const { study } = useConfig();
  const { goPrevious } = useHomeContext();

  // null while the pre-check runs (loader), then the roles still to declare.
  const [visibleRoles, setVisibleRoles] = useState<ProfileType[] | null>(null);
  const [values, setValues] = useState<Record<number, string>>(() => {
    const seeded: Record<number, string> = {};
    (initialDeclarations ?? []).forEach((declaration) => {
      seeded[declaration.study_role_id] = String(declaration.count);
    });
    return seeded;
  });
  const [showErrors, setShowErrors] = useState(false);
  const completedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const resolveVisibleRoles = async (): Promise<void> => {
      let counts: Record<number, number> = {};
      if (externalId) {
        try {
          const { data } = await postFamilyPrecheck(
            { studyId: study.id },
            { external_id: externalId }
          );
          counts = data?.counts ?? {};
        } catch {
          // Fail-soft: show every role — the server's zero-members guard dedupes at consent.
          counts = {};
        }
      }
      if (cancelled) return;
      const remaining = targetRoles.filter(
        (role) => (counts[role.id ?? -1] ?? 0) === 0
      );
      if (!remaining.length) {
        // Family already declared (other parent / investigator) — nothing to ask.
        if (!completedRef.current) {
          completedRef.current = true;
          onCompleted([]);
        }
        return;
      }
      setVisibleRoles(remaining);
    };
    resolveVisibleRoles();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (visibleRoles === null) return <ResonanceLoader />;

  const errorForRole = (role: ProfileType): string | null => {
    const { min, max } = roleBounds(role);
    const raw = (values[role.id ?? -1] ?? "").trim();
    if (!raw) return t("form_error_required");
    const parsed = Number(raw);
    if (!Number.isInteger(parsed) || parsed < min || parsed > max) {
      return t("answer_number_constraint_min_max", { min, max });
    }
    return null;
  };

  const handleNext = (): void => {
    const hasError = visibleRoles.some((role) => errorForRole(role) !== null);
    if (hasError) {
      setShowErrors(true);
      return;
    }
    onCompleted(
      visibleRoles.map((role) => ({
        study_role_id: role.id as number,
        count: Number((values[role.id ?? -1] ?? "").trim()),
      }))
    );
  };

  return (
    <Container>
      <Content>
        <Title>{t("home_family_title")}</Title>
        {visibleRoles.map((role) => (
          <div key={role.id}>
            <Question
              question={buildCountQuestion(
                role,
                t("home_family_count_fallback", { role: role.profile })
              )}
              answer={values[role.id ?? -1] ?? ""}
              onAnswerChange={(value) =>
                setValues((current) => ({
                  ...current,
                  [role.id ?? -1]: String(value ?? ""),
                }))
              }
              onEnterNext={handleNext}
              readText={readText}
              cardMargin="12px 20px"
            />
            {showErrors && errorForRole(role) && (
              <CompositionFieldError>{errorForRole(role)}</CompositionFieldError>
            )}
          </div>
        ))}
      </Content>

      <NextPrevButtons onPreviousClick={goPrevious} onNextClick={handleNext} />
    </Container>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const Title = styled.h1`
  margin: 18px 50px 4px;
  font-size: 20px;
  font-weight: bold;
  color: var(--app-color-text, #393939);
`;

const CompositionFieldError = styled(FieldError)`
  margin: 6px 25px 0;
`;

export default DisplayFamilyComposition;
